/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Andrey Dubov
 * @version 1.23.0
 * @since 1.13.0
 */

import { ArrayElement } from '../../../../types/globalTypes';
import { ESubscriberIdActions } from '../../types';
import { IProductAccordions, IProductItem } from './types';

export interface IProductResponse {
    products: Array<{
        actions: {
            [ESubscriberIdActions.productDisconnect]?: { disabled: boolean; reason?: string };
        };
        activationDate?: string;
        description: string;
        descriptionLink?: string;
        groups: number[];
        payment?: {
            periodLength: number;
            unit: string;
            value: number;
        };
        plannedDate?: Array<{
            type: 'activation' | 'deactivation';
            date: string;
        }>;
        productOfferingId: number;
        title: string;
        personalAccount?: boolean;
    }>;
}

export interface IProductCategoriesResponse {
    productCategories: Array<{
        id: number;
        name: string;
        orderNum: number;
        parentId: number;
    }>;
}

type TProductListItemResponse = ArrayElement<IProductResponse['products']>;
type TProductListMapping = { [categoryId: number]: IProductItem[] };

const mapProductListItemResponse =
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ groups, productOfferingId, personalAccount, actions, ...product }: TProductListItemResponse): IProductItem => ({
        ...product,
        isPersonalAccountProduct: !!personalAccount,
        id: productOfferingId,
        actions: {
            [ESubscriberIdActions.productDisconnect]: {
                isDisabled: actions[ESubscriberIdActions.productDisconnect]?.disabled,
                isVisible: !!actions[ESubscriberIdActions.productDisconnect],
                reason: actions[ESubscriberIdActions.productDisconnect]?.reason,
            },
        },
    });

const productListMapping =
    (response: IProductResponse): TProductListMapping =>
        response.products.reduce((result: TProductListMapping, product: TProductListItemResponse) => {
            const mappedProduct = mapProductListItemResponse(product);
            product.groups.forEach((categoryId: number) => {
                if (result[categoryId]) {
                    result[categoryId].push(mappedProduct);
                } else {
                    result[categoryId] = [mappedProduct];
                }
            });
            return result;
        }, {});

type TProductCategoryResponse = ArrayElement<IProductCategoriesResponse['productCategories']>;
const productCategoryResponse =
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ orderNum, parentId, name, ...category }: TProductCategoryResponse): IProductAccordions => ({
        ...category,
        children: [],
        parent: parentId,
        products: [],
        title: name,
    });

export const productCategoriesMapping = (
    responseCategories: IProductCategoriesResponse,
    responseProducts: IProductResponse,
    rootCategoryId: number,
): IProductAccordions[] => {
    const mapCategoryIdToIndex: { [key: number]: number } = {};
    const roots: IProductAccordions[] = [];

    const mappedProducts: TProductListMapping = productListMapping(responseProducts);
    const mappedCategories: IProductAccordions[] = responseCategories.productCategories
        .sort((a: TProductCategoryResponse, b: TProductCategoryResponse) => a.orderNum - b.orderNum)
        .map(productCategoryResponse);

    for (let i = 0; i < mappedCategories.length; i += 1) {
        mapCategoryIdToIndex[mappedCategories[i].id] = i; // initialize the map
    }
    for (let i = 0; i < mappedCategories.length; i += 1) {
        const node = mappedCategories[i];
        node.products = mappedProducts[node.id];
        if (node.parent !== rootCategoryId) {
            if (mapCategoryIdToIndex[node.parent] === undefined) continue;

            mappedCategories[mapCategoryIdToIndex[node.parent]].children?.push(node);
        } else {
            roots.push(node);
        }
    }

    for (let i = 0; i < mappedCategories.length; i += 1) {
        const node = mappedCategories[i];
        if (node.parent !== rootCategoryId) {
            const parentCategoryIdIndex = mapCategoryIdToIndex[node.parent];
            if (parentCategoryIdIndex === undefined) continue;

            const productIds = node.products?.map(product => product.id);
            mappedCategories[parentCategoryIdIndex].products?.forEach((item, index) => {
                if (productIds?.includes(item.id)) {
                    mappedCategories[parentCategoryIdIndex].products?.splice(index, 1);
                }
            });
        }
    }

    const accordions = roots.filter(isItemEmpty);
    accordions.forEach(category => {
        const subCategoryCount = category.children?.reduce(
            (totalNumber: number, childCategory: IProductAccordions) => {
                return totalNumber + (childCategory.products?.length || 0);
            }, 0);
        category.count = (category.products?.length || 0) + (subCategoryCount || 0);
    });
    return accordions;
};

const isObjEmpty = (obj: IProductAccordions): boolean => !obj.products?.length && !obj.children?.length;

const isItemEmpty = (obj: IProductAccordions): boolean => {
    if (isObjEmpty(obj)) {
        return false;
    } else if (obj.children?.length) {
        return !!obj.products?.length || obj.children.filter(isItemEmpty).length !== 0;
    }
    return true;
};
